import { EventBus } from '@pp/toolkit';

import { Logger } from '../../logger/Logger';
import { IntervalScheduler } from '../../utils/IntervalScheduler';

import {
  SOCKET_CONNECTION_STATUS,
  SOCKET_CLOSE_REASON,
  type Socket,
  type SocketConstructor,
  type SocketSendPayload,
  type WebSocketBaseEvents,
  type SocketCommunicationStrategy,
  type WebSockcetServiceOptions,
} from './types';

/**
 * **`WebSocketService` is a wrapper around the WebSocket API**
 *
 * MessageType describes unified type of payload for all parsed messages
 *
 * SocketAdapter is a class with own implementation of a web socket
 *
 * SocketAdapterConstructor is a constructor of the SocketAdapter class
 */
export class WebSocketService<
  ParsedMessageType,
  SocketAdapter extends Socket = NativeWebSocket,
  SocketAdapterConstructor extends SocketConstructor<SocketAdapter> = SocketConstructor<SocketAdapter>,
> extends EventBus<WebSocketBaseEvents<ParsedMessageType>> {
  private _strategy: SocketCommunicationStrategy<ParsedMessageType> | null = null;
  private _connectionStatus: SOCKET_CONNECTION_STATUS = SOCKET_CONNECTION_STATUS.DISCONNECTED;
  private socketCloseReason: SOCKET_CLOSE_REASON = SOCKET_CLOSE_REASON.OTHER;
  private reconnectAttempts = 0;
  private pendingPong = false;

  private validateConnectionScheduler: IntervalScheduler | null = null;
  private reconnectScheduler: IntervalScheduler | null = null;

  public socket: SocketAdapter | null = null;
  private _baseUrl: string = '';
  private _searchParams: URLSearchParams = new URLSearchParams({});

  public get baseUrl(): string {
    return this._baseUrl;
  }

  public set baseUrl(value: string) {
    if (this.options?.useProxyConnection) {
      this._baseUrl = this.prependWithProxy(value);
    } else {
      this._baseUrl = value;
    }
  }

  public get url(): string {
    if (!this.baseUrl) return '';
    const params = this.searchParams.toString();
    return params ? `${this.baseUrl}?${params}` : this.baseUrl;
  }

  public get searchParams(): URLSearchParams {
    return this._searchParams;
  }

  public set searchParams(value: URLSearchParams) {
    this._searchParams = value;
  }

  // bound handlers
  private _messageHandler = this.processMessage.bind(this);
  private _openHandler = this.openHandler.bind(this);
  private _closeHandler = this.closeHandler.bind(this);
  private _errorHandler = this.errorHandler.bind(this);

  private get strategy(): SocketCommunicationStrategy<ParsedMessageType> {
    if (!this._strategy) {
      throw new Error('Socket communication strategy is not set');
    }
    return this._strategy;
  }

  setStrategy(strategy: SocketCommunicationStrategy<ParsedMessageType>): void {
    this._strategy = strategy;
  }

  // DI websocket class for easier websocket mocking during testing
  constructor(
    private SocketConstructor: SocketAdapterConstructor,
    private options?: WebSockcetServiceOptions
  ) {
    super();

    if (!this.options?.keepAlive) return;

    this.validateConnectionScheduler = new IntervalScheduler({
      interval: this.options.keepAlive.connectionValidationInterval,
      config: {
        abortOnReSchedule: true,
      },
      onTick: this.validateConnection.bind(this),
      onStop: (): void => {
        this.pendingPong = false; // reset pending pong flag on pinging stop
      },
    });

    this.reconnectScheduler = new IntervalScheduler({
      interval: this.options.keepAlive.reconnectionInterval,
      config: {
        abortOnReSchedule: true,
      },
      onTick: this.reconnect.bind(this),
      onStop: (): void => {
        this.reconnectAttempts = 0; // reset reconnect attempts on reconnection stop
      },
    });
  }

  public get isAlive(): boolean {
    return this.socket?.readyState === this.SocketConstructor.OPEN;
  }

  set connectionStatus(status: SOCKET_CONNECTION_STATUS) {
    if (this._connectionStatus !== status) this.dispatchEvent('connectionStatusChange', status);
    this._connectionStatus = status;
  }

  get connectionStatus(): SOCKET_CONNECTION_STATUS {
    return this._connectionStatus;
  }

  public send(data: SocketSendPayload): void {
    if (this.socket && this.isAlive) {
      this.socket.send(data);
    }
  }

  public restartCommunication(): void {
    if (!this.url) {
      return;
    }
    if (this.isAlive) this.killCommunication();
    this.openSocket();
  }

  public openSocket(onInit?: () => void): void {
    this.pendingPong = false;

    switch (this.connectionStatus) {
      case SOCKET_CONNECTION_STATUS.DISCONNECTED:
        this.connectionStatus = SOCKET_CONNECTION_STATUS.CONNECTING;
        break;
      case SOCKET_CONNECTION_STATUS.TERMINATED:
        this.connectionStatus = SOCKET_CONNECTION_STATUS.RECONNECTING;
        break;
    }

    if (!this.url) throw new Error('WebSocket URL is not set');

    if (this.socket && this.isAlive) this.socket.close();

    this.socket = new this.SocketConstructor(this.url);
    onInit?.();
    this.addSocketListeners();
    this.validateConnectionScheduler?.start();
  }

  public closeSocket(reason: SOCKET_CLOSE_REASON): void {
    if (!this.socket) return;
    this.socket.close();
    this.socket.removeEventListener('message', this._messageHandler);
    this.socketCloseReason = reason;
  }

  public killCommunication(): void {
    if (!this.socket) return;

    this.connectionStatus = SOCKET_CONNECTION_STATUS.TERMINATED;
    this.closeSocket(SOCKET_CLOSE_REASON.KILL);
    this.cleanUp();
  }

  private reconnect(): void {
    if (!this.options?.keepAlive) return;

    if (this.isAlive) this.closeSocket(SOCKET_CLOSE_REASON.RECONNECT);

    this.reconnectAttempts++;

    // reconnection failed
    if (this.reconnectAttempts > this.options.keepAlive.reconnectLimit) {
      this.reconnectScheduler?.stop();
      this.connectionStatus = SOCKET_CONNECTION_STATUS.CONNECTION_LOST;
    } else {
      // attempt to re-establish a new connection
      this.connectionStatus = SOCKET_CONNECTION_STATUS.RECONNECTING;
      this.openSocket();
    }
  }

  private validateConnection(): void {
    // start pinging on pong response timeout only if reconnection is not in progress yet
    if (!this.reconnectScheduler?.isRunning && this.pendingPong) {
      this.validateConnectionScheduler?.stop();
      this.reconnectScheduler?.start();
    } else {
      if (!this.isAlive) return;
      this.pendingPong = true;
      this.send(this.strategy.assemblePingMessage());
    }
  }

  private prependWithProxy(url: string): string {
    return location.origin.replace('http', 'ws') + '/proxy/' + url;
  }

  private processMessage(event: MessageEvent): void {
    const message = this.strategy.parseMessage(event);

    if (this.strategy.recognizePongMessage(message)) {
      this.pendingPong = false;
      return;
    }

    this.dispatchEvent('processedMessage', message);
    this.dispatchEvent('rawMessage', event);
  }
  private addSocketListeners(): void {
    if (!this.socket) return;

    this.socket.addEventListener('open', this._openHandler);
    this.socket.addEventListener('message', this._messageHandler);
    this.socket.addEventListener('close', this._closeHandler);
    this.socket.addEventListener('error', this._errorHandler);
  }

  private removeSocketListeners(): void {
    if (!this.socket) return;

    this.socket.removeEventListener('open', this._openHandler);
    this.socket.removeEventListener('message', this._messageHandler);
    this.socket.removeEventListener('close', this._closeHandler);
    this.socket.removeEventListener('error', this._errorHandler);
  }

  private closeHandler(event: CloseEvent): void {
    this.dispatchEvent('close', event);

    if (this.socketCloseReason === SOCKET_CLOSE_REASON.OTHER) {
      this.reconnectScheduler?.start();
    }

    Logger.logEvent('socket_close', { additional: `${this.url}:${this.socketCloseReason}` });
  }

  private openHandler(event: Event): void {
    this.reconnectScheduler?.stop();

    this.socketCloseReason = SOCKET_CLOSE_REASON.OTHER;

    switch (this.connectionStatus) {
      case SOCKET_CONNECTION_STATUS.RECONNECTING:
        this.connectionStatus = SOCKET_CONNECTION_STATUS.RECONNECTED;
        break;
      case SOCKET_CONNECTION_STATUS.CONNECTING:
        this.connectionStatus = SOCKET_CONNECTION_STATUS.CONNECTED;
        break;
    }

    this.dispatchEvent('open', event);
  }

  private errorHandler(error: Event): void {
    this.dispatchEvent('error', error);
  }

  private cleanUp(): void {
    this.validateConnectionScheduler?.stop();
    this.reconnectScheduler?.stop();
    this.removeSocketListeners();
  }
}

/* Real WebSocket implementation of the Socket interface*/
export class NativeWebSocket extends WebSocket implements Socket {}
